import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  User,
  Phone,
  MapPin,
  Package,
  TrendingUp,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Crown,
  Calendar,
  DollarSign,
  Mail,
  UserPlus,
} from "lucide-react";
import LoadingSpinner from "../../components/LoadingSpinner";
import { API_BASE_URL } from "../../utils/apiConfig";
import { getLeadDisplayAddress } from "../../utils/leadAddress";
import { useLanguage } from "../../contexts/LanguageContext";

interface CurrentUser {
  id: string;
  username?: string;
  email?: string;
  role?: string;
  permissions?: string[];
}

interface Lead {
  id: string;
  name: string;
  phone: string;
  email?: string;
  address?: string;
  city?: string;
  area?: string;
  notes?: string;
  stage?: 'new' | 'basic' | 'good' | 'potential' | 'customer' | 'vip';
  leadOwner?: { userId: string; username?: string; email?: string; role?: string } | null;
  matchingOrders?: Array<{
    orderId: number | string;
    status: number;
    date: Date | string;
    products?: Array<{
      name: string;
      quantity: number;
      image?: string;
      unitPrice?: number;
      totalPrice?: number;
      currency?: string;
    }>;
    productsString?: string;
    total?: number;
    currency?: string;
    image?: string;
    bexStatus?: string;
    wcStatus?: string;
    reason?: string;
    isSuccessful?: boolean;
    source?: string;
    name?: string;
    phone?: string;
    address?: string;
    city?: string;
    country?: string;
    comment?: string;
    createdBy?: {
      userId: string;
      username: string;
      role: string;
    };
  }>;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

interface HistoryEntry {
  id: string;
  action: string;
  userId: string;
  user?: {
    userId: string;
    username: string;
    role: string;
    email?: string;
  };
  details: any;
  timestamp: string;
}

interface Stats {
  successfulOrders: number;
  totalOrders: number;
  totalSpent: number;
  failedOrders: number;
  successRate: string;
}

interface LeadDetailPageProps {
  currentUser?: CurrentUser | null;
}

const LeadDetailPage: React.FC<LeadDetailPageProps> = ({ currentUser }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const listSearch = (location.state as { fromListSearch?: string } | null)?.fromListSearch ?? "";
  const { t } = useLanguage();
  const [lead, setLead] = useState<Lead | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [takingFromLeads, setTakingFromLeads] = useState(false);

  const canEdit = (currentUser?.permissions?.includes("leads.edit") ?? false);

  useEffect(() => {
    fetchLeadDetails();
  }, [id]);

  const handleTakeFromLeads = async () => {
    if (!id || !canEdit || !currentUser) return;
    if (!window.confirm(t("leads.takeFromLeadsConfirm"))) return;
    setTakingFromLeads(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/api/leads/${id}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          leadOwner: {
            userId: currentUser.id,
            username: currentUser.username ?? "",
            email: currentUser.email ?? "",
            role: currentUser.role,
            timestamp: new Date().toISOString(),
          },
        }),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (!data.success) throw new Error(data.error || "Failed to take lead");
      await fetchLeadDetails();
    } catch (err) {
      console.error("Error taking lead:", err);
      alert(err instanceof Error ? err.message : "Failed to take lead");
    } finally {
      setTakingFromLeads(false);
    }
  };

  const fetchLeadDetails = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `${API_BASE_URL}/api/leads/${id}`,
        {
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setLead(data.data.lead);
        setHistory(data.data.history || []);
        setStats(data.data.stats || null);
      } else {
        throw new Error(data.error || "Failed to fetch lead");
      }
    } catch (err) {
      console.error("Error fetching lead:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch lead");
    } finally {
      setLoading(false);
    }
  };

  const getStageBadge = (stage?: string) => {
    if (!stage) return null;
    const stages = {
      new: { label: "New", color: "bg-gray-100 text-gray-800" },
      basic: { label: "Basic", color: "bg-blue-100 text-blue-800" },
      good: { label: "Good", color: "bg-green-100 text-green-800" },
      potential: { label: "Potential", color: "bg-yellow-100 text-yellow-800" },
      customer: { label: "Customer", color: "bg-purple-100 text-purple-800" },
      vip: { label: "VIP", color: "bg-amber-100 text-amber-800" },
    };
    const stageInfo = stages[stage as keyof typeof stages] || stages.new;
    return (
      <span className={`px-3 py-1 text-sm font-medium rounded-full ${stageInfo.color} flex items-center`}>
        {stage === 'vip' && <Crown className="w-4 h-4 mr-1" />}
        {stageInfo.label}
      </span>
    );
  };

  const getStatusBadge = (status: number) => {
    if (status === 10) {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 flex items-center">
          <CheckCircle2 className="w-3 h-3 mr-1" />
          Complete
        </span>
      );
    } else if (status === 5) {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 flex items-center">
          <XCircle className="w-3 h-3 mr-1" />
          Cancelled
        </span>
      );
    }
    
    return (
      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
        Status: {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
            <p className="text-sm text-red-800">{error || "Lead not found"}</p>
          </div>
        </div>
        <button
          onClick={() => navigate(`/leads${listSearch}`)}
          className="mt-4 px-4 py-2 bg-[#264a3e] text-white rounded-md hover:bg-[#1a3329]"
        >
          Back to Leads
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(`/leads${listSearch}`)}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <User className="w-6 h-6 mr-2" />
              {lead.name}
            </h1>
            <p className="text-sm text-gray-500 mt-1">Lead Details</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {canEdit && currentUser && lead.leadOwner && (
            <button
              onClick={handleTakeFromLeads}
              disabled={takingFromLeads}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md bg-[#264a3e] text-white hover:bg-[#1a3329] disabled:opacity-50 disabled:cursor-not-allowed"
              title={t("leads.takeFromLeads")}
            >
              <UserPlus className="w-4 h-4" />
              {takingFromLeads ? "..." : t("leads.takeFromLeads")}
            </button>
          )}
          {getStageBadge(lead.stage)}
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Orders</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalOrders}</p>
              </div>
              <Package className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Successful</p>
                <p className="text-2xl font-bold text-green-600">{stats.successfulOrders}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Spent</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalSpent.toLocaleString()} RSD</p>
              </div>
              <DollarSign className="w-8 h-8 text-amber-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Success Rate</p>
                <p className="text-2xl font-bold text-gray-900">{stats.successRate}%</p>
              </div>
              <TrendingUp className="w-8 h-8 text-purple-500" />
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Lead Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Lead Information</h2>
          <div className="space-y-4">
            <div className="flex items-start">
              <User className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="text-sm font-medium text-gray-900">{lead.name}</p>
              </div>
            </div>
            <div className="flex items-start">
              <Phone className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="text-sm font-medium text-gray-900">{lead.phone}</p>
              </div>
            </div>
            {lead.email && (
              <div className="flex items-start">
                <Mail className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="text-sm font-medium text-gray-900">{lead.email}</p>
                </div>
              </div>
            )}
            {getLeadDisplayAddress(lead) && (
              <div className="flex items-start">
                <MapPin className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Address</p>
                  <p className="text-sm font-medium text-gray-900">{getLeadDisplayAddress(lead)}</p>
                </div>
              </div>
            )}
            {lead.notes && (
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Notes</p>
                  <p className="text-sm font-medium text-gray-900">{lead.notes}</p>
                </div>
              </div>
            )}
            <div className="flex items-start">
              <Calendar className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500">Created</p>
                <p className="text-sm font-medium text-gray-900">
                  {new Date(lead.createdAt).toLocaleString()}
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <Clock className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500">Last Updated</p>
                <p className="text-sm font-medium text-gray-900">
                  {new Date(lead.updatedAt).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Order History */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Order History</h2>
          {lead.matchingOrders && lead.matchingOrders.length > 0 ? (
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {lead.matchingOrders.map((order, idx) => {
                // Get first product for main image
                const firstProduct = order.products && order.products.length > 0 ? order.products[0] : null;
                const productImage = firstProduct?.image || order.image;
                const imageUrl = productImage 
                  ? (productImage.startsWith('http') 
                      ? productImage 
                      : `https://naturatherapy.mk/image/cache/${productImage.replace(/^catalog\//, '')}`)
                  : null;
                
                return (
                  <div key={idx} className="border border-gray-200 rounded-md p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-4">
                      {/* Order Image */}
                      {imageUrl ? (
                        <div className="flex-shrink-0">
                          <img
                            src={imageUrl}
                            alt={firstProduct?.name || "Order"}
                            className="w-20 h-20 object-cover rounded-lg border-2 border-gray-200"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = "none";
                            }}
                          />
                        </div>
                      ) : (
                        <div className="flex-shrink-0 w-20 h-20 bg-gray-100 rounded-lg border-2 border-gray-200 flex items-center justify-center">
                          <Package className="w-10 h-10 text-gray-400" />
                        </div>
                      )}
                      
                      {/* Order Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-900">
                            Order #{order.orderId}
                          </span>
                          {getStatusBadge(order.status)}
                        </div>
                        
                        {/* Products with Images */}
                        {(order.products && order.products.length > 0) || order.productsString ? (
                          <div className="mt-2 mb-2">
                            <p className="text-xs font-medium text-gray-700 mb-2">Products:</p>
                            {order.products && order.products.length > 0 ? (
                              <div className="space-y-2">
                                {order.products.map((product: any, pIdx: number) => {
                                  const itemImage = product.image;
                                  const itemImageUrl = itemImage 
                                    ? (itemImage.startsWith('http') 
                                        ? itemImage 
                                        : `https://naturatherapy.mk/image/cache/${itemImage.replace(/^catalog\//, '')}`)
                                    : null;
                                  
                                  return (
                                    <div key={pIdx} className="flex items-center space-x-2 text-xs text-gray-600">
                                      {itemImageUrl ? (
                                        <img
                                          src={itemImageUrl}
                                          alt={product.name}
                                          className="w-10 h-10 object-cover rounded border border-gray-200"
                                          onError={(e) => {
                                            (e.target as HTMLImageElement).style.display = "none";
                                          }}
                                        />
                                      ) : (
                                        <div className="w-10 h-10 bg-gray-100 rounded border border-gray-200 flex items-center justify-center">
                                          <Package className="w-5 h-5 text-gray-400" />
                                        </div>
                                      )}
                                      <div className="flex-1">
                                        <span className="font-medium">{product.name}</span>
                                        {product.quantity > 1 && <span> (x{product.quantity})</span>}
                                        {product.unitPrice && (
                                          <span className="text-gray-500 ml-2">
                                            - {product.unitPrice.toLocaleString()} {product.currency || 'MKD'}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-xs text-gray-600">{order.productsString}</p>
                            )}
                          </div>
                        ) : null}
                        
                        {/* Total */}
                        {order.total && order.total > 0 && (
                          <div className="text-sm text-gray-700 mb-2">
                            <strong>Total:</strong> {order.total.toLocaleString()} {order.currency || 'RSD'}
                            {order.isSuccessful ? ' (Paid)' : ' (Not Paid)'}
                          </div>
                        )}
                        
                        {/* Customer Info */}
                        {(order.name || order.phone || order.address || order.city) && (
                          <div className="text-xs text-gray-500 mb-2 space-y-1">
                            {order.name && <div><strong>Customer:</strong> {order.name}</div>}
                            {order.phone && <div><strong>Phone:</strong> {order.phone}</div>}
                            {(order.address || order.city) && (
                              <div>
                                <strong>Address:</strong> {[order.address, order.city].filter(Boolean).join(', ')}
                              </div>
                            )}
                            {order.country && <div><strong>Country:</strong> {order.country}</div>}
                          </div>
                        )}
                        
                        {/* Comment */}
                        {order.comment && (
                          <div className="text-xs text-gray-600 mb-2 italic">
                            "{order.comment}"
                          </div>
                        )}
                        
                        {/* Reason */}
                        {order.reason && (
                          <div className="text-sm text-red-600 mb-2">
                            <strong>Reason:</strong> {order.reason}
                          </div>
                        )}
                        
                        {/* Source & Created By */}
                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                          {order.source && (
                            <div className="text-xs text-gray-500">
                              Source: {order.source}
                            </div>
                          )}
                          {order.createdBy && (
                            <div className="text-xs text-gray-500">
                              Created by: {order.createdBy.username || order.createdBy.userId}
                              {order.createdBy.role && ` (${order.createdBy.role})`}
                            </div>
                          )}
                        </div>
                        
                        {/* Date */}
                        {order.date && (
                          <div className="text-xs text-gray-500 mt-2">
                            {new Date(order.date).toLocaleString()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No order history available</p>
          )}
        </div>
      </div>

      {/* Action History */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Action History</h2>
        {history.length > 0 ? (
          <div className="space-y-3">
            {history.map((entry) => (
              <div key={entry.id} className="border-l-4 border-[#264a3e] pl-4 py-2">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900 capitalize">
                        {entry.action.replace(/_/g, ' ')}
                      </p>
                      {entry.user && (
                        <span className="text-xs text-gray-500">
                          by {entry.user.username}
                          {entry.user.role && (
                            <span className="ml-1 text-gray-400">
                              ({entry.user.role.replace(/-/g, ' ')})
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                    {entry.details && Object.keys(entry.details).length > 0 && (
                      <div className="mt-2 space-y-1">
                        {entry.details.fieldChanges && Array.isArray(entry.details.fieldChanges) && entry.details.fieldChanges.length > 0 ? (
                          // Show field changes with from/to values
                          entry.details.fieldChanges.map((change: any, idx: number) => (
                            <div key={idx} className="text-xs bg-gray-50 p-2 rounded border border-gray-200">
                              <span className="font-medium text-gray-700">{change.field}:</span>{" "}
                              <span className="text-red-600 line-through">
                                {change.oldValue !== null && change.oldValue !== undefined 
                                  ? (typeof change.oldValue === 'object' ? JSON.stringify(change.oldValue) : String(change.oldValue))
                                  : "(empty)"}
                              </span>{" "}
                              →{" "}
                              <span className="text-green-600 font-medium">
                                {change.newValue !== null && change.newValue !== undefined 
                                  ? (typeof change.newValue === 'object' ? JSON.stringify(change.newValue) : String(change.newValue))
                                  : "(empty)"}
                              </span>
                            </div>
                          ))
                        ) : entry.details.fieldsChanged && Array.isArray(entry.details.fieldsChanged) ? (
                          // Fallback: show just field names if fieldChanges not available
                          <div className="text-xs text-gray-500">
                            Fields changed: {entry.details.fieldsChanged.join(", ")}
                          </div>
                        ) : entry.details.orderId ? (
                          // Order created - show all order creation entries (they are separate entries in history)
                          <div className="text-xs bg-blue-50 p-2 rounded border border-blue-200">
                            <span className="font-medium text-blue-700">Order #{entry.details.orderId} created</span>
                            {entry.details.orderName && (
                              <span className="text-blue-600 ml-2">- {entry.details.orderName}</span>
                            )}
                            {entry.details.orderPrice && (
                              <span className="text-blue-600 ml-2">
                                ({entry.details.orderPrice} {entry.details.orderCurrency || 'MKD'})
                              </span>
                            )}
                            {entry.details.createdBy && (
                              <div className="text-blue-600 mt-1">
                                by {entry.details.createdBy.username || entry.details.createdBy.userId}
                                {entry.details.createdBy.role && ` (${entry.details.createdBy.role})`}
                              </div>
                            )}
                          </div>
                        ) : (
                          // Fallback for other details
                          <p className="text-xs text-gray-500">
                            {JSON.stringify(entry.details)}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 ml-4">
                    {new Date(entry.timestamp).toLocaleString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No action history available</p>
        )}
      </div>
    </div>
  );
};

export default LeadDetailPage;
